import { Alert, InputAdornment, Typography } from '@mui/material'
import {
  CampaignCategory,
  ReviewSection,
  ReviewTarget,
  FormatVersion,
  CampaignCostHandlingMethod,
} from '~/generated/graphql'
import { Controller, useFormContext } from 'react-hook-form'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { z } from 'zod'
import { categoryField } from './type'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { CustomAmountTextField } from '~/components/common/CustomAmountTextField'
import { FC } from 'react'
import { selectCostHandlingMethodSchemaFields } from '~/features/campaign_input/SelectCostHandlingMethod'
import { getSponsorServicePriceTitle } from '~/lib/feature/getCampaignTitle'

export const sponsorServicePriceSchemaFields = {
  sponsorServicePriceIntax: z.coerce.number().min(0, { message: '0円以上で入力してください' }).max(1_000_000, {
    message: '100万円以下で入力してください',
  }),
  ...categoryField,
  ...selectCostHandlingMethodSchemaFields,
}

export const sponsorServicePriceSchema = z.object({ ...sponsorServicePriceSchemaFields })

export const validateSponsorServicePrice = (data: z.infer<typeof sponsorServicePriceSchema>, ctx: z.RefinementCtx) => {
  if (data.sponsorServicePriceIntax === 0 && data.costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign)
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '金額が正しくありません。実際にサービスを注文した際にかかる費用をご入力ください。',
      path: ['sponsorServicePriceIntax'],
    })
}

type Props = CampaignInputComponentProps

export const SponsorServicePrice = ({ description, tooltip }: Props) => {
  const { control, getValues } = useFormContext<z.infer<typeof sponsorServicePriceSchema>>()
  const category = getValues('category')
  const costHandlingMethod = getValues('costHandlingMethod')

  return (
    <CampaignInputComponent
      title={`${getSponsorServicePriceTitle(costHandlingMethod, category)}（税込）`}
      required={true}
      description={description}
      tooltip={tooltip}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.SponsorServicePrice],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      {costHandlingMethod === CampaignCostHandlingMethod.ExpenseCampaign && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {category === CampaignCategory.Service ? (
            <SponsorServicePriceServiceExpenseInfo />
          ) : (
            <SponsorServicePriceProductInfo />
          )}
        </Alert>
      )}
      <Controller
        name="sponsorServicePriceIntax"
        control={control}
        render={({ field: { ref, value, onChange, ...field }, fieldState: { error } }) => (
          <CustomAmountTextField
            {...field}
            inputRef={ref}
            type="number"
            placeholder="0"
            value={value}
            onChange={(e) => {
              const val = e.target.value
              // 数値または空文字だけを許容
              if (val === '' || /^[0-9]+$/.test(val)) {
                onChange(val)
              }
            }}
            fullWidth
            error={!!error}
            helperText={<ValidationErrorMessage error={error?.message} />}
            slotProps={{
              input: {
                inputMode: 'numeric',
                endAdornment: <InputAdornment position="end">円</InputAdornment>,
              },
            }}
          />
        )}
      />
    </CampaignInputComponent>
  )
}

export const SponsorServicePriceServiceExpenseInfo: FC = () => {
  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        サービスの税込金額（＝実際にサービスを注文した際にかかる費用）をご入力ください。
        この金額が、インフルエンサーの立替申請額の上限（サービス体験者1名あたり）となります。
        <br />
        なお、広告主様のご負担額は、立替費用還元率を適用した金額となります。実際の立替申請額は「本項目にご入力いただいた金額 × サービス体験人数（インフルエンサー + 同伴者数）」で計算され、そこに還元率を適用した金額が広告主様の負担額となります。
        <br />
        ※万一、体験時商材の購入価格が変動していた場合も立替金額の上限は変更されません。
      </Typography>
    </>
  )
}

export const SponsorServicePriceProductInfo: FC = () => {
  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        送料・手数料を含めた、実際に購入時にかかる税込の総額をご入力ください。
        この金額が、インフルエンサーの立替申請額の上限（PR投稿1件あたり）となります。
        <br />
        より多くのインフルエンサーからの応募を集めるため、追加で発生しうる費用（各種手数料など）も考慮した金額設定をおすすめします。
        <br />
        なお、広告主様のご負担額は、購入時にかかった実費に立替費用還元率を適用した金額となり、必ずしも入力いただいた上限額まで精算されるわけではありません。
      </Typography>
    </>
  )
}
